import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const DONUT_COLORS = [
  "#487FFF",
  "#45B369",
  "#FF9F29",
  "#9B8AFB",
  "#EF4444",
  "#06B6D4",
];

function truncateLabel(name, max = 18) {
  const s = String(name || "");
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

function aggregateStores(storeStatistics) {
  if (!storeStatistics || typeof storeStatistics !== "object") return [];

  return Object.keys(storeStatistics)
    .map((storeName) => {
      const storeData = storeStatistics[storeName];
      let totalOrders = 0;
      let totalRevenue = 0;
      Object.keys(storeData || {}).forEach((date) => {
        const day = storeData[date];
        totalOrders += day?.order || 0;
        const price = Number(day?.totalPrice) || 0;
        totalRevenue += price;
      });
      return {
        storeName,
        totalOrders,
        totalRevenue: parseFloat(Number(totalRevenue).toFixed(2)),
      };
    })
    .filter((s) => s.totalOrders > 0 || s.totalRevenue > 0)
    .sort((a, b) => b.totalRevenue - a.totalRevenue);
}

function buildColumnOrderShare(rowsByOrders, maxPoints = 4) {
  const total = rowsByOrders.reduce((s, r) => s + (r.totalOrders || 0), 0) || 1;
  if (!rowsByOrders.length) return [];

  const top = rowsByOrders.slice(0, maxPoints);
  const rest = rowsByOrders.slice(maxPoints);
  const rows = top.map((r) => ({
    name: truncateLabel(r.storeName, 16),
    pct: Number((((r.totalOrders || 0) / total) * 100).toFixed(2)),
  }));

  if (rest.length > 0) {
    const restSum = rest.reduce((s, r) => s + (r.totalOrders || 0), 0);
    rows.push({
      name: `Khác (${rest.length})`,
      pct: Number(((restSum / total) * 100).toFixed(2)),
    });
  }

  return rows;
}

function buildDonutSlices(rows, maxSlices, valueKey) {
  if (!rows.length) return [];
  const top = rows.slice(0, maxSlices);
  const rest = rows.slice(maxSlices);
  const restVal = rest.reduce((sum, r) => sum + (Number(r[valueKey]) || 0), 0);

  const list = top.map((r) => ({
    name: truncateLabel(r.storeName),
    value: Number(r[valueKey]) || 0,
  }));

  if (restVal > 0) {
    list.push({
      name: `Khác (${rest.length} store)`,
      value: restVal,
    });
  }

  return list;
}

/**
 * Port logic từ DashboardUpgrade `FulfilStoreShareCharts` — Column % đơn + Donut revenue (Recharts).
 */
function FulfilStoreShareCharts({ storeStatistics = {} }) {
  const rows = useMemo(() => aggregateStores(storeStatistics), [storeStatistics]);

  const rowsByOrders = useMemo(
    () => [...rows].sort((a, b) => b.totalOrders - a.totalOrders),
    [rows]
  );
  const rowsByRevenue = useMemo(
    () => [...rows].sort((a, b) => b.totalRevenue - a.totalRevenue),
    [rows]
  );

  const columnData = useMemo(
    () => buildColumnOrderShare(rowsByOrders, 4),
    [rowsByOrders]
  );

  const donutData = useMemo(
    () => buildDonutSlices(rowsByRevenue, 4, "totalRevenue"),
    [rowsByRevenue]
  );

  const totalRevenue = useMemo(
    () => donutData.reduce((a, b) => a + (Number(b.value) || 0), 0),
    [donutData]
  );

  if (!rows.length) {
    return (
      <Card sx={{ border: "1px solid #e9ecef", borderRadius: 2, height: "100%" }}>
        <CardContent sx={{ py: 6, textAlign: "center" }}>
          <MDTypography variant="body2" color="text">
            Không có dữ liệu store để hiển thị tỉ lệ
          </MDTypography>
        </CardContent>
      </Card>
    );
  }

  return (
    <MDBox display="flex" flexDirection="column" gap={2} height="100%">
      <Card sx={{ border: "1px solid #e9ecef", borderRadius: 2, flex: 1 }}>
        <MDBox sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e9ecef", bgcolor: "#fafafa" }}>
          <MDTypography variant="subtitle2" fontWeight={600}>
            Tỉ lệ đơn theo store
          </MDTypography>
          <MDTypography variant="caption" color="text.secondary" display="block">
            % đơn trên tổng — tối đa 4 store + Khác; mỗi cột một màu (chú thích dưới biểu đồ)
          </MDTypography>
        </MDBox>
        <CardContent sx={{ pt: 1 }}>
          {columnData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={columnData} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#E5E7EB" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={columnData.length > 3 ? -25 : 0} textAnchor={columnData.length > 3 ? "end" : "middle"} height={columnData.length > 3 ? 70 : 30} />
                  <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => [`${Number(v).toFixed(2)}%`, "Tỉ lệ"]} />
                  <Bar
                    dataKey="pct"
                    radius={[6, 6, 0, 0]}
                    maxBarSize={48}
                    isAnimationActive={false}
                    label={{ position: "top", formatter: (v) => `${Number(v).toFixed(1)}%`, fontSize: 11, fill: "#334155" }}
                  >
                    {columnData.map((entry, index) => (
                      <Cell key={`order-bar-${entry.name}`} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <MDBox display="flex" flexWrap="wrap" gap={1.5} justifyContent="center" alignItems="center" mt={1.5} px={1}>
                {columnData.map((entry, index) => (
                  <MDBox key={`legend-${entry.name}`} display="flex" alignItems="center" gap={0.75}>
                    <MDBox
                      component="span"
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "4px",
                        bgcolor: DONUT_COLORS[index % DONUT_COLORS.length],
                        flexShrink: 0,
                      }}
                    />
                    <MDTypography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", lineHeight: 1.25 }}>
                      {entry.name}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            </>
          ) : (
            <MDTypography variant="body2" color="text.secondary" textAlign="center" py={4}>
              Không có đơn
            </MDTypography>
          )}
        </CardContent>
      </Card>

      <Card sx={{ border: "1px solid #e9ecef", borderRadius: 2, flex: 1 }}>
        <MDBox sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e9ecef", bgcolor: "#fafafa" }}>
          <MDTypography variant="subtitle2" fontWeight={600}>
            Tỉ lệ revenue theo store
          </MDTypography>
          <MDTypography variant="caption" color="text.secondary" display="block">
            Donut — phần trăm trên tổng revenue, tối đa 4 store + Khác
          </MDTypography>
        </MDBox>
        <CardContent sx={{ pt: 1 }}>
          <ResponsiveContainer width="100%" height={190}>
            <PieChart>
              <Pie
                data={donutData}
                cx="50%"
                cy="50%"
                innerRadius={42}
                outerRadius={64}
                paddingAngle={2}
                dataKey="value"
                nameKey="name"
                isAnimationActive={false}
                label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
              >
                {donutData.map((_, i) => (
                  <Cell key={`cell-${i}`} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => {
                  const v = Number(value);
                  const pct = totalRevenue ? ((v / totalRevenue) * 100).toFixed(2) : "0";
                  const money =
                    Math.abs(v) >= 1000
                      ? `$${(v / 1000).toFixed(2)}k`
                      : `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                  return [`${money} (${pct}%)`, "Revenue"];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
          <MDTypography variant="caption" color="text.secondary" display="block" textAlign="center" mt={1}>
            Tổng revenue:{" "}
            <strong>
              {totalRevenue >= 1000
                ? `$${(totalRevenue / 1000).toFixed(2)}k`
                : `$${totalRevenue.toFixed(2)}`}
            </strong>
          </MDTypography>
        </CardContent>
      </Card>
    </MDBox>
  );
}

FulfilStoreShareCharts.propTypes = {
  storeStatistics: PropTypes.object,
};

export default FulfilStoreShareCharts;
